import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ApiClient,
  ProblemDetailError,
  type ApprovalReviewResponse,
  type SetupStatusResponse,
} from "./api/client";
import { TopBar } from "./components/TopBar";
import { Sidebar } from "./components/Sidebar";
import { HaltBanner } from "./components/HaltBanner";
import { SizeGuard } from "./components/SizeGuard";
import {
  ApprovalModal,
  type ApprovalAcks,
  type ApprovalModalIssue,
} from "./components/ApprovalModal";
import { LoosenConfigModal } from "./components/LoosenConfigModal";
import { DashboardScreen } from "./screens/DashboardScreen";
import { BriefingScreen } from "./screens/BriefingScreen";
import { DayTradesScreen } from "./screens/DayTradesScreen";
import { ClosedTradesScreen } from "./screens/ClosedTradesScreen";
import { JournalScreen } from "./screens/JournalScreen";
import { LoginScreen } from "./screens/LoginScreen";
import { SetupAdminScreen } from "./screens/SetupAdminScreen";
import { StubScreen } from "./screens/StubScreen";
import { SystemScreen } from "./screens/SystemScreen";
import { ConfigScreen, type ConfigTab } from "./screens/ConfigScreen";
import { FirstRunWizard } from "./screens/FirstRunWizard";
import { NewsActivationConfirmation } from "./screens/NewsActivationConfirmation";
import { WatchlistScreen } from "./screens/WatchlistScreen";
import { MoonshotSignalsScreen } from "./screens/MoonshotSignalsScreen";
import { ChartScreen } from "./screens/ChartScreen";
import { SignalHistoryScreen } from "./screens/SignalHistoryScreen";
import { CryptoScreen } from "./screens/CryptoScreen";
import { ForexScreen } from "./screens/ForexScreen";
import { ResearchScreen } from "./screens/ResearchScreen";
import { useFixtures, type GeneratedSignal } from "./api/dataSource";
import { useDataSource } from "./api/useDataSource";
import { clearSessionToken, hasSession } from "./lib/session";
import { coolingOffFromNow } from "./lib/coolingOff";
import { computeDisclosureHash } from "./lib/disclosures";
import type {
  ActiveHaltBanner,
  Pending,
  PendingConfigChange,
  RiskLimit,
  RiskLimitKey,
  TradePreview,
  View,
} from "./types";

const stubs: Partial<Record<View, { title: string; note: string }>> = {
  investments: {
    title: "Investments",
    note: "Long-term investment holdings are not yet implemented. This view will show buy-and-hold positions with annual review tracking when the investment portfolio layer lands.",
  },
  system: {
    title: "System",
    note: "Service health, reconciliation status, AI state. Not yet ported.",
  },
};

// Build-time toggle: when set (VITE_DISABLE_AUTH=1 at build), the
// UI skips the login screen + /me probe entirely and renders the
// dashboard directly. Pair with TRADINGPLATFORM_AUTH_DISABLED=1
// on the app process so the server middleware is off too.
//
// This is an operator escape hatch for browsers that block fetch
// against self-signed certs (e.g. Brave). Phase 5 hardening item:
// replace self-signed with mkcert / Let's Encrypt so this toggle
// is no longer needed.
const DISABLE_AUTH: boolean =
  (import.meta.env.VITE_DISABLE_AUTH as string | undefined) === "1";

const ACTIVATED_AT_STORAGE_KEY = "tradeplatform_system_activated_at";

// Confirm-and-place idempotency keys, one per approval_id
// (2026-06-10 safety review, finding 9). A retry after a timeout must
// reuse the SAME key so the server-side idempotency claim can dedupe
// the placement; regenerating per click defeated that. Module-level so
// the key survives modal close/reopen; entries are pruned when the
// approval leaves the pending list (see the effect in App).
const confirmPlaceKeys = new Map<string, string>();

const confirmPlaceKeyFor = (approvalId: string): string => {
  const existing = confirmPlaceKeys.get(approvalId);
  if (existing !== undefined) return existing;
  const randomPart =
    typeof window !== "undefined" && window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const key = `ui-confirm-place-${randomPart}`;
  confirmPlaceKeys.set(approvalId, key);
  return key;
};

type ActivationStatus = {
  activated: boolean;
  activatedAt: string | null;
};

type ApprovalOpenStatus = "idle" | "loading" | "loaded" | "failed";
type ApprovalPreviewOverride = {
  preview?: TradePreview | null;
  currentPricePreview?: TradePreview | null;
};

const readLocalActivationStatus = (): ActivationStatus => {
  if (typeof window === "undefined") {
    return { activated: false, activatedAt: null };
  }
  const activatedAt = window.localStorage.getItem(ACTIVATED_AT_STORAGE_KEY);
  return {
    activated: activatedAt !== null,
    activatedAt,
  };
};

const readSetupToken = (): string | null => {
  if (typeof window === "undefined") return null;
  const value = new URLSearchParams(window.location.search).get("setup_token");
  return value && value.trim() ? value : null;
};

const clearSetupTokenFromUrl = () => {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (!url.searchParams.has("setup_token")) return;
  url.searchParams.delete("setup_token");
  const qs = url.searchParams.toString();
  window.history.replaceState(
    {},
    "",
    `${url.pathname}${qs ? `?${qs}` : ""}${url.hash}`,
  );
};

const haltBannersFromEvents = (
  events: { type: string; summary: string }[],
): ActiveHaltBanner[] => {
  const banners: ActiveHaltBanner[] = [];
  const seen = new Set<ActiveHaltBanner["variant"]>();
  const push = (banner: ActiveHaltBanner) => {
    if (seen.has(banner.variant)) return;
    seen.add(banner.variant);
    banners.push(banner);
  };

  for (const event of events) {
    if (event.type === "pf_gap_intervention") {
      push({
        variant: "pf_gap_intervention",
        title: "PF gap intervention",
        body: event.summary,
      });
    } else if (event.type === "system_health_circuit_breaker_tripped") {
      push({
        variant: "generic_halted",
        title: "Signal generation circuit breaker",
        body: event.summary,
      });
    } else if (
      event.type === "daily_loss_limit_breached" ||
      event.type === "weekly_loss_limit_breached"
    ) {
      push({
        variant: "bankroll_cap_intervention",
        title: "Loss limit breached",
        body: event.summary,
      });
    } else if (event.type === "system_halted") {
      push({
        variant: "generic_halted",
        title: "System halted",
        body: event.summary,
      });
    }
  }
  return banners;
};

const issueFromApprovalReview = (
  review: ApprovalReviewResponse,
): ApprovalModalIssue | null => {
  if (review.actionable) {
    return null;
  }
  const codes = new Set(review.issues.map((issue) => issue.code));
  const detail =
    review.issues
      .map((issue) => issue.detail)
      .filter(Boolean)
      .join(" ") || "Approval is no longer actionable. Reprice the signal.";
  if (
    codes.has("price_drift_exceeds_tolerance") ||
    codes.has("stop_breached") ||
    codes.has("quote_unavailable")
  ) {
    return { kind: "price_drift", detail, blocking: true };
  }
  if (codes.has("limit_check_failed")) {
    return { kind: "limits_changed", detail, blocking: true };
  }
  return { kind: "approval_blocked", detail, blocking: true };
};

const tradePreviewFromWire = (
  wire: NonNullable<ApprovalReviewResponse["preview"]>,
): TradePreview => ({
  derivedQty: Number(wire.derived_qty),
  actualRiskDollars: Number(wire.actual_risk_dollars),
  targetGainDollars: Number(wire.target_gain_dollars),
  riskRewardRatio: Number(wire.risk_reward_ratio),
  basisPrice: Number(wire.basis_price),
  stopPrice: Number(wire.stop_price),
  targetPrice: Number(wire.target_price),
  riskPerShare: Number(wire.risk_per_share),
  gainPerShare: Number(wire.gain_per_share),
});

const previewOverrideFromReview = (
  review: ApprovalReviewResponse,
): ApprovalPreviewOverride => ({
  preview: review.preview ? tradePreviewFromWire(review.preview) : null,
  currentPricePreview: review.current_price_preview
    ? tradePreviewFromWire(review.current_price_preview)
    : null,
});

const issueFromApprovalError = (e: unknown): ApprovalModalIssue => {
  const fallback = e instanceof Error ? e.message : "Approval placement failed";
  if (!(e instanceof ProblemDetailError)) {
    return {
      kind: "order_placement_failed",
      detail: fallback,
      blocking: true,
    };
  }
  const haystack = `${e.type} ${e.title} ${e.detail} ${e.instance}`
    .toLowerCase();
  // Phase 9-UI: the forex activation gate rejects at the token-minting
  // layer with a dedicated 403 problem type. Matched FIRST (before the
  // generic keyword buckets) and rendered with its own message — this
  // is a standing governance gate (Override 5 Tier A), not a transient
  // placement failure, so "try again" framing would mislead.
  if (e.type.endsWith("/forex_trading_disabled")) {
    return {
      kind: "approval_blocked",
      detail:
        "Forex trading is gated off (Override 5 Tier A). Signals can be " +
        "reviewed, but order confirmation is blocked until the operator " +
        "enables forex trading after Phase 9e validation.",
      blocking: true,
    };
  }
  if (
    haystack.includes("price") ||
    haystack.includes("quote") ||
    haystack.includes("drift") ||
    haystack.includes("stop")
  ) {
    return {
      kind: "price_drift",
      detail: e.detail || "The market price changed before placement.",
      blocking: true,
    };
  }
  if (haystack.includes("limit")) {
    return {
      kind: "limits_changed",
      detail:
        e.detail ||
        "Risk limits changed before placement. Review the approval again.",
      blocking: true,
    };
  }
  if (
    haystack.includes("halt") ||
    haystack.includes("expired") ||
    haystack.includes("token")
  ) {
    return {
      kind: "approval_blocked",
      detail: e.detail || fallback,
      blocking: true,
    };
  }
  return {
    kind: "order_placement_failed",
    detail: e.detail || fallback,
    blocking: true,
  };
};

const approvalErrorDetail = (e: unknown): string =>
  e instanceof Error ? e.message : "Failed to load approval details.";

const ApprovalLookupModal = ({
  approvalId,
  status,
  error,
  onClose,
}: {
  approvalId: string;
  status: ApprovalOpenStatus;
  error: string | null;
  onClose: () => void;
}) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const loading = status === "loading" || status === "idle";
  const title = loading ? "Loading approval" : "Approval unavailable";
  const detail = loading
    ? "Refreshing pending approvals before review."
    : error ??
      "This approval is not in the current pending list. It may already be placed, expired, repriced, or removed by the latest refresh.";
  return (
    <div className="tp-modal-backdrop" role="presentation">
      <div
        className="tp-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="approval-lookup-title"
      >
        <div className="tp-modal-head">
          <div>
            <div className="eyebrow">Approval review</div>
            <h2 id="approval-lookup-title">{title}</h2>
            <div className="sub num">{approvalId}</div>
          </div>
        </div>
        <div className="tp-modal-body">
          <div
            role={loading ? "status" : "alert"}
            aria-live={loading ? "polite" : "assertive"}
            style={{
              padding: "10px 12px",
              background: loading
                ? "var(--tp-ink-1)"
                : "rgba(255, 190, 80, 0.12)",
              border: loading
                ? "var(--tp-border-soft)"
                : "1px solid rgba(255, 190, 80, 0.45)",
              borderRadius: 4,
              color: loading ? "var(--tp-fg-2)" : "var(--tp-warn)",
              fontSize: 12,
            }}
          >
            {detail}
          </div>
        </div>
        <div className="tp-modal-foot">
          <span className="num" style={{ color: "var(--tp-fg-3)", fontSize: 11 }}>
            <span className="tp-kbd">Esc</span> cancels
          </span>
          <div className="actions">
            <button
              type="button"
              className="tp-btn tp-btn-secondary"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const App = () => {
  const fixturesActive = useFixtures();
  // Phase 5 D-A: session is now HttpOnly so the UI can't read
  // the cookie directly. In fixture mode we still use the
  // client-side cookie (set by the LoginScreen on submit). In
  // real-backend mode we probe /api/v1/me on mount; 200 → in,
  // 401 → out. The api client's onUnauthorized hook flips us
  // back to the login screen on stale-cookie 401s mid-session.
  // DISABLE_AUTH short-circuits everything (escape hatch).
  const [authenticated, setAuthenticated] = useState<boolean>(
    () => DISABLE_AUTH || (fixturesActive && hasSession()),
  );
  const [authProbed, setAuthProbed] = useState<boolean>(
    DISABLE_AUTH || fixturesActive,
  );
  const setupToken = useMemo(readSetupToken, []);
  const [setupStatus, setSetupStatus] =
    useState<SetupStatusResponse | null>(null);
  const [setupProbed, setSetupProbed] = useState<boolean>(
    DISABLE_AUTH || fixturesActive,
  );

  // Phase 5 D-A: API client wired with the 401 handler.
  const [apiClient] = useState<ApiClient>(
    () =>
      new ApiClient({
        onUnauthorized: () => setAuthenticated(false),
      }),
  );

  useEffect(() => {
    if (fixturesActive || DISABLE_AUTH) return;
    let cancelled = false;
    apiClient
      .getSetupStatus(setupToken)
      .then((status) => {
        if (cancelled) return;
        setSetupStatus(status);
        setSetupProbed(true);
      })
      .catch(() => {
        if (cancelled) return;
        setSetupStatus(null);
        setSetupProbed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [fixturesActive, apiClient, setupToken]);

  useEffect(() => {
    if (fixturesActive || DISABLE_AUTH) return;
    let cancelled = false;
    apiClient
      .me()
      .then(() => {
        if (cancelled) return;
        setAuthenticated(true);
        setAuthProbed(true);
      })
      .catch(() => {
        if (cancelled) return;
        setAuthenticated(false);
        setAuthProbed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [fixturesActive, apiClient]);

  const handleLogout = () => {
    if (fixturesActive) {
      clearSessionToken();
      setAuthenticated(false);
      return;
    }
    apiClient
      .logout()
      .catch(() => {
        // Best-effort: even if the server call fails, we want
        // the UI to surface the login screen.
      })
      .finally(() => setAuthenticated(false));
  };

  // Phase 5 (b) RE-CLOSE (MED-B-2 fix): React Rules of Hooks
  // require every hook to run on every render in the same
  // order. Pre-fix, the early returns at `!authProbed` and
  // `!authenticated` short-circuited BEFORE the hooks below
  // executed — so a flip from "loading" → "authenticated"
  // would call hooks in a different order than the prior render
  // (running zero hooks on render N, then 8 hooks on render N+1).
  // React surfaces this as a console warning in dev and as
  // unstable component state in prod.
  //
  // Fix: every `useState` / `useEffect` lives above the early
  // returns. The conditional renders happen further down via
  // the `appBody` variable selection. The hook order is now
  // identical across every render.
  const [view, setView] = useState<View>("dashboard");
  const [configTab, setConfigTab] = useState<ConfigTab>("risk");
  const [approvalId, setApprovalId] = useState<string | null>(null);
  const [approvalIssue, setApprovalIssue] =
    useState<ApprovalModalIssue | null>(null);
  const [approvalPreviewOverrides, setApprovalPreviewOverrides] = useState<
    Record<string, ApprovalPreviewOverride>
  >({});
  const [placingApprovalId, setPlacingApprovalId] = useState<string | null>(
    null,
  );
  const [approvalOpenStatus, setApprovalOpenStatus] =
    useState<ApprovalOpenStatus>("idle");
  const [approvalOpenError, setApprovalOpenError] = useState<string | null>(
    null,
  );
  const placingApprovalRef = useRef<string | null>(null);
  const approvalReviewRequestRef = useRef(0);

  // First-run wizard (UX §4 / PRD §6.7). Default false so the
  // workstation opens directly in dev; the user launches the wizard
  // from the System sidebar item. Production wiring auto-shows it
  // when `first_run_complete` is missing from the app config table.
  const [showWizard, setShowWizard] = useState<boolean>(false);
  const [showNewsActivation, setShowNewsActivation] =
    useState<boolean>(false);
  const [disclosurePromptRequired, setDisclosurePromptRequired] =
    useState<boolean>(false);
  const [activationStatus, setActivationStatus] = useState<ActivationStatus>(
    readLocalActivationStatus,
  );

  // Phase 7a D8: monthly AI cost status + quality-tier warning.
  // Phase 5 D-Live Slice 3: live data from the backend. Replaces
  // direct fixture imports. The hook fetches all seven UI lists
  // on mount and exposes a manual refresh() for screens that
  // mutate state (confirm an approval, queue a config change).
  //
  // Note: this hook fires on mount even before authentication
  // succeeds. The hook itself short-circuits via the
  // `authenticated` flag inside its useEffect — it must do so
  // because we can't conditionally call it from here without
  // re-introducing the hook-order bug.
  const ds = useDataSource();
  const accounts = ds.accounts;
  const pending = ds.pending;
  const positions = ds.positions;
  const positionTrades = ds.positionTrades;
  const investments = ds.investments;
  const baskets = ds.baskets;
  // Phase 5 (d): direct refs (no local mirror needed — these are
  // read-only display data with no optimistic-update pattern).
  const systemHealth = ds.systemHealth;
  const recentEvents = ds.recentEvents;
  const aiCostStatus = ds.aiCostStatus;
  const haltBanners = useMemo(
    () => haltBannersFromEvents(recentEvents),
    [recentEvents],
  );
  const reviewPendingApproval = useCallback(
    (id: string) => {
      const requestId = approvalReviewRequestRef.current + 1;
      approvalReviewRequestRef.current = requestId;
      setApprovalIssue(null);
      void ds
        .reviewApproval(id)
        .then((review) => {
          if (approvalReviewRequestRef.current !== requestId) {
            return;
          }
          setApprovalPreviewOverrides((curr) => ({
            ...curr,
            [id]: previewOverrideFromReview(review),
          }));
          setApprovalIssue(issueFromApprovalReview(review));
        })
        .catch((e) => {
          if (approvalReviewRequestRef.current !== requestId) {
            return;
          }
          setApprovalIssue(issueFromApprovalError(e));
        });
    },
    [ds.reviewApproval],
  );

  // Risk-tab state. Lifted to App so the Dashboard pending-change banner
  // can read pendingChanges without having to round-trip through Config.
  // Mirrored from the data source so screens can mutate locally
  // (e.g. tighten-now applies optimistically) and we re-sync on
  // refresh().
  const [riskLimits, setRiskLimits] = useState<RiskLimit[]>([]);
  const [pendingChanges, setPendingChanges] = useState<
    PendingConfigChange[]
  >([]);
  useEffect(() => {
    setRiskLimits(ds.riskLimits);
  }, [ds.riskLimits]);
  useEffect(() => {
    setPendingChanges(ds.pendingChanges);
  }, [ds.pendingChanges]);

  // Prune cached confirm-and-place idempotency keys once their
  // approval leaves the pending list (placed elsewhere, expired, or
  // repriced) so a future approval cannot collide with a stale key.
  useEffect(() => {
    const ids = new Set(pending.map((p) => p.approvalId));
    for (const id of Array.from(confirmPlaceKeys.keys())) {
      if (!ids.has(id)) {
        confirmPlaceKeys.delete(id);
      }
    }
  }, [pending]);

  // Phase 5 (b) RE-CLOSE (MED-B-2 follow-up): now that the
  // useDataSource hook fires on mount BEFORE auth completes
  // (necessary to keep hook order stable across renders), the
  // first refresh() may 401 against the protected dashboard
  // endpoints. Re-fetch when the user transitions to
  // authenticated so the post-login render shows real data
  // instead of the failed-on-401 empty state.
  useEffect(() => {
    if (authenticated && !fixturesActive) {
      void ds.refresh();
    }
  }, [authenticated, fixturesActive, ds.refresh]);

  useEffect(() => {
    if (!authenticated) return;
    if (fixturesActive) {
      setActivationStatus(readLocalActivationStatus());
      return;
    }
    let cancelled = false;
    apiClient
      .getSystemActivationStatus()
      .then((status) => {
        if (cancelled) return;
        setActivationStatus({
          activated: status.activated,
          activatedAt: status.activated_at,
        });
      })
      .catch((e) => {
        // Fail-soft: the System tab can still render. If the wizard
        // just completed in this browser, show that short-lived local
        // hint until the endpoint is reachable again.
        // eslint-disable-next-line no-console
        console.error("[activation-status] refresh failed", e);
        if (!cancelled) {
          setActivationStatus(readLocalActivationStatus());
        }
      });
    return () => {
      cancelled = true;
    };
  }, [authenticated, fixturesActive, apiClient]);

  useEffect(() => {
    if (!authenticated || fixturesActive) return;
    let cancelled = false;
    computeDisclosureHash()
      .then((hash) => apiClient.getDisclosuresStatus(hash))
      .then((status) => {
        if (cancelled || !status.needs_reprompt) return;
        setDisclosurePromptRequired(true);
        setShowWizard(true);
      })
      .catch((e) => {
        // The backend defaults fail-closed when no durable disclosure
        // state exists. A network error is logged and retried on next
        // launch rather than blocking the shell from rendering.
        // eslint-disable-next-line no-console
        console.error("[disclosures-status] refresh failed", e);
      });
    return () => {
      cancelled = true;
    };
  }, [authenticated, fixturesActive, apiClient]);

  const [loosenKey, setLoosenKey] = useState<RiskLimitKey | null>(null);
  const [autoApprovalsUpdating, setAutoApprovalsUpdating] = useState(false);
  const [coolingOffDays, setCoolingOffDays] = useState(7);

  useEffect(() => {
    if (!authenticated || fixturesActive) return;
    let cancelled = false;
    apiClient
      .getCoolingOffConfig()
      .then((config) => {
        if (!cancelled) {
          setCoolingOffDays(config.cooling_off_days);
        }
      })
      .catch((e) => {
        // Fail-soft: keep the safety default visible.
        // eslint-disable-next-line no-console
        console.error("[cooling-off-config] refresh failed", e);
      });
    return () => {
      cancelled = true;
    };
  }, [authenticated, fixturesActive, apiClient]);

  // Operator-initiated signal generation. Calls the backend, then
  // immediately refreshes the pending-approvals list so new rows
  // appear in the dashboard without a manual page reload.
  const handleGenerateSignals = useCallback(async () => {
    const result = await ds.generateSignals();
    void ds.refresh(); // fire-and-forget: pending list reloads in background
    return result;
  }, [ds.generateSignals, ds.refresh]);

  const handleAutoApprovalsChange = useCallback(
    async (enabled: boolean, minConfidence: number) => {
      setAutoApprovalsUpdating(true);
      try {
        await ds.setAutoApprovalsConfig({ enabled, minConfidence });
      } finally {
        setAutoApprovalsUpdating(false);
      }
    },
    [ds.setAutoApprovalsConfig],
  );

  const handleRepriceSignal = useCallback(
    async (
      signal: GeneratedSignal,
      riskDollars?: string,
    ): Promise<string> => {
      const approvalId = await ds.repriceSignalApproval(signal, riskDollars);
      await ds.refresh();
      setApprovalOpenStatus("loaded");
      setApprovalOpenError(null);
      setApprovalId(approvalId);
      reviewPendingApproval(approvalId);
      return approvalId;
    },
    [ds.repriceSignalApproval, ds.refresh, reviewPendingApproval],
  );

  const handleResizeApproval = useCallback(
    async (approval: Pending, riskDollars: string): Promise<void> => {
      setApprovalIssue(null);
      const resized = await ds.resizeApproval(approval.approvalId, riskDollars);
      setApprovalPreviewOverrides((curr) => ({
        ...curr,
        [approval.approvalId]: {
          preview: resized.preview,
          currentPricePreview: null,
        },
      }));
      await ds.refresh();
      reviewPendingApproval(approval.approvalId);
    },
    [ds.resizeApproval, ds.refresh, reviewPendingApproval],
  );

  const handleApprovePending = useCallback(
    async (approval: Pending, acks: ApprovalAcks) => {
      if (placingApprovalRef.current !== null) {
        setApprovalIssue({
          kind: "approval_blocked",
          detail:
            placingApprovalRef.current === approval.approvalId
              ? "This approval is already being submitted to the broker."
              : "Another approval is currently being submitted. Wait for it to finish before confirming this trade.",
          blocking: true,
        });
        return;
      }
      placingApprovalRef.current = approval.approvalId;
      setPlacingApprovalId(approval.approvalId);
      setApprovalOpenStatus("loaded");
      setApprovalOpenError(null);
      try {
        const review = await ds.reviewApproval(approval.approvalId);
        setApprovalPreviewOverrides((curr) => ({
          ...curr,
          [approval.approvalId]: previewOverrideFromReview(review),
        }));
        const reviewIssue = issueFromApprovalReview(review);
        if (reviewIssue) {
          setApprovalIssue(reviewIssue);
          await ds.refresh();
          return;
        }
        // Stable per-approval key: a retry after a timeout reuses it
        // so the server can replay instead of double-placing.
        await ds.confirmAndPlaceApproval(
          approval.approvalId,
          confirmPlaceKeyFor(approval.approvalId),
          acks.user_acknowledged_live_real_money,
        );
        confirmPlaceKeys.delete(approval.approvalId);
        setApprovalId(null);
        setApprovalOpenStatus("idle");
        setApprovalOpenError(null);
        await ds.refresh();
      } finally {
        placingApprovalRef.current = null;
        setPlacingApprovalId(null);
      }
    },
    [ds.confirmAndPlaceApproval, ds.refresh, ds.reviewApproval],
  );

  const openApproval = useCallback((id: string) => {
    setApprovalId(id);
    setApprovalOpenStatus("loading");
    setApprovalOpenError(null);
    reviewPendingApproval(id);
    void ds
      .refresh()
      .then(() => {
        setApprovalOpenStatus("loaded");
      })
      .catch((e) => {
        setApprovalOpenStatus("failed");
        setApprovalOpenError(approvalErrorDetail(e));
      });
  }, [ds.refresh, reviewPendingApproval]);

  // Phase 5 D-A: probing /me first; show a brief blank screen.
  // Early returns AFTER all hooks above so React's hook-order
  // invariant holds across renders.
  if (!authProbed || !setupProbed) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
        }}
      >
        <span style={{ color: "var(--tp-fg-3, #888)", fontSize: 13 }}>
          loading…
        </span>
      </div>
    );
  }
  if (!authenticated) {
    if (setupStatus?.setup_required) {
      return (
        <SetupAdminScreen
          apiClient={apiClient}
          setupToken={setupToken}
          tokenValid={setupStatus.token_valid}
          onCreated={() => {
            clearSetupTokenFromUrl();
            setSetupStatus({
              setup_required: false,
              admin_exists: true,
              token_required: false,
              token_valid: null,
            });
            setDisclosurePromptRequired(true);
            setShowWizard(true);
            setAuthenticated(true);
          }}
        />
      );
    }
    return (
      <LoginScreen
        apiClient={apiClient}
        onLogin={() => setAuthenticated(true)}
      />
    );
  }

  const ptOverdueCount = positionTrades.filter(
    (p) => p.reviewOverdueDays,
  ).length;
  const invOverdueCount = investments.filter(
    (i) => i.reviewStatus === "overdue" || i.reviewStatus === "due",
  ).length;

  const baseActivePending = approvalId
    ? pending.find((p) => p.approvalId === approvalId)
    : null;
  const activeOverride =
    baseActivePending && approvalId
      ? approvalPreviewOverrides[approvalId]
      : undefined;
  // When the server review supplied a Decimal-computed preview, its
  // actual_risk_dollars overrides the client-side float estimate so
  // the "I accept the $X risk" acknowledgment quotes the server's
  // number (2026-06-10 safety review, finding 8). Client math remains
  // only as the fallback when no server preview exists.
  const activePending =
    baseActivePending && activeOverride
      ? {
          ...baseActivePending,
          ...activeOverride,
          ...(activeOverride.preview
            ? { risk: activeOverride.preview.actualRiskDollars }
            : {}),
        }
      : baseActivePending;

  const loosenLimit = loosenKey
    ? riskLimits.find((l) => l.key === loosenKey)
    : null;

  // Risk-tab actions ----------------------------------------------------

  const tightenLimit = (key: RiskLimitKey, newValue: number) => {
    setRiskLimits((curr) =>
      curr.map((l) => (l.key === key ? { ...l, value: newValue } : l)),
    );
  };

  const submitLoosen = (newValue: number, reflection: string) => {
    if (!loosenLimit) {
      return;
    }
    const stamp = coolingOffFromNow(coolingOffDays);
    const id = `pcc-${Date.now()}`;
    const change: PendingConfigChange = {
      id,
      key: loosenLimit.key,
      label: loosenLimit.label,
      currentValue: loosenLimit.value,
      newValue,
      unit: loosenLimit.unit,
      submittedAtDisplay: new Date()
        .toISOString()
        .replace("T", " ")
        .slice(0, 16) + " UTC",
      activatesAtDisplay: stamp.display,
      activatesAtMs: stamp.ms,
      reflection,
    };
    setPendingChanges((curr) => [...curr, change]);
    setLoosenKey(null);
  };

  const cancelPending = (id: string) => {
    setPendingChanges((curr) => curr.filter((p) => p.id !== id));
  };

  // -------------------------------------------------------------------

  let screen;
  if (view === "dashboard") {
    screen = (
      <DashboardScreen
        onNav={setView}
        onConfigTab={(t) => setConfigTab(t as ConfigTab)}
        onOpenApproval={openApproval}
        onQuickApprove={openApproval}
        pendingConfigChanges={pendingChanges}
        onCancelPendingChange={cancelPending}
        accounts={accounts}
        pending={pending}
        positions={positions}
        positionReassessments={ds.positionReassessments}
        positionTrades={positionTrades}
        investments={investments}
        baskets={baskets}
        riskLimits={riskLimits}
        listRiskLimits={ds.listRiskLimits}
        getRiskSnapshot={ds.getRiskSnapshot}
        systemHealth={systemHealth}
        recentEvents={recentEvents}
        cryptoSummary={ds.cryptoSummary}
        cryptoValidationRuns={ds.cryptoValidationRuns}
        forexConfig={ds.forexConfig}
        forexPairs={ds.forexPairs}
        onGenerateSignals={handleGenerateSignals}
        autoApprovalsEnabled={ds.autoApprovalsConfig?.enabled ?? true}
        autoApprovalsMinConfidence={
          ds.autoApprovalsConfig?.minConfidence ?? 70
        }
        autoApprovalsUpdating={autoApprovalsUpdating}
        onAutoApprovalsChange={handleAutoApprovalsChange}
        onRepriceSignal={handleRepriceSignal}
        onPreviewSignal={ds.previewSignalApproval}
        onHaltAll={ds.haltAll}
        onResumeAll={ds.resumeAll}
      />
    );
  } else if (view === "briefing") {
    screen = (
      <BriefingScreen
        pending={pending}
        positionReassessments={ds.positionReassessments}
        recentEvents={recentEvents}
        onOpenApproval={openApproval}
        onGenerateSignals={handleGenerateSignals}
        listSignalHistory={ds.listSignalHistory}
        onRepriceSignal={handleRepriceSignal}
        onPreviewSignal={ds.previewSignalApproval}
      />
    );
  } else if (view === "signal-history") {
    screen = (
      <SignalHistoryScreen
        listSignalHistory={ds.listSignalHistory}
        onOpenApproval={openApproval}
        onRepriceSignal={handleRepriceSignal}
        onPreviewSignal={ds.previewSignalApproval}
      />
    );
  } else if (view === "charts") {
    screen = (
      <ChartScreen
        listChartSymbols={ds.listChartSymbols}
        getChartSnapshot={ds.getChartSnapshot}
      />
    );
  } else if (view === "watchlist") {
    screen = (
      <WatchlistScreen
        pending={pending}
        positions={positions}
        onOpenApproval={openApproval}
      />
    );
  } else if (view === "moonshots") {
    screen = (
      <MoonshotSignalsScreen
        listMoonshotSignals={ds.listMoonshotSignals}
        rankMoonshotTopTen={ds.rankMoonshotTopTen}
        createResearchReport={ds.createResearchReport}
      />
    );
  } else if (view === "crypto") {
    screen = (
      <CryptoScreen
        apiClient={apiClient}
        accounts={accounts}
        pending={pending}
        recentEvents={recentEvents}
        cryptoValidationRuns={ds.cryptoValidationRuns}
        listCryptoValidationRuns={ds.listCryptoValidationRuns}
        usingFixtures={ds.usingFixtures}
        onOpenApproval={openApproval}
        onRefresh={ds.refresh}
      />
    );
  } else if (view === "forex") {
    screen = (
      <ForexScreen
        accounts={accounts}
        pending={pending}
        recentEvents={recentEvents}
        forexConfig={ds.forexConfig}
        forexPairs={ds.forexPairs}
        forexAccount={ds.forexAccount}
        onOpenApproval={openApproval}
      />
    );
  } else if (view === "trades") {
    screen = (
      <DayTradesScreen
        accounts={accounts}
        positions={positions}
        positionReassessments={ds.positionReassessments}
        ds={ds}
      />
    );
  } else if (view === "positions") {
    screen = (
      <ClosedTradesScreen
        accounts={accounts}
        listClosedStockTrades={(args) => ds.listClosedStockTrades(args)}
      />
    );
  } else if (view === "research") {
    screen = (
      <ResearchScreen
        listResearchReports={ds.listResearchReports}
        listSavedResearchEvaluations={ds.listSavedResearchEvaluations}
        evaluateSymbol={ds.evaluateSymbol}
        acceptEvaluation={ds.acceptEvaluation}
        createApprovalFromEvaluation={ds.createApprovalFromEvaluation}
        runAgentReview={ds.runAgentReview}
        runShadowAgentReviews={ds.runShadowAgentReviews}
        getLatestWorkflowStatus={ds.getLatestWorkflowStatus}
        resumeBatchResearchEnrichment={ds.resumeBatchResearchEnrichment}
        getChartSnapshot={ds.getChartSnapshot}
        listDecisionReflections={ds.listDecisionReflections}
        generateDecisionReflections={ds.generateDecisionReflections}
        accounts={ds.accounts}
      />
    );
  } else if (view === "journal") {
    screen = <JournalScreen recentEvents={recentEvents} />;
  } else if (view === "config") {
    screen = (
      <ConfigScreen
        tab={configTab}
        onTab={setConfigTab}
        riskLimits={riskLimits}
        pendingChanges={pendingChanges}
        coolingOffDays={coolingOffDays}
        onCoolingOffDaysChange={setCoolingOffDays}
        accounts={accounts}
        positions={positions}
        pending={pending}
        systemHealth={systemHealth}
        recentEvents={recentEvents}
        listRiskLimits={ds.listRiskLimits}
        getSectorLimits={ds.getSectorLimits}
        setSectorLimits={ds.setSectorLimits}
        listStrategyRoutes={ds.listStrategyRoutes}
        updateStrategyRoute={ds.updateStrategyRoute}
        onTighten={tightenLimit}
        onOpenLoosen={(key) => setLoosenKey(key)}
        onCancelPending={cancelPending}
        newsProviderName={aiCostStatus?.news_provider_name ?? "none"}
        qualityTierWarning={aiCostStatus?.quality_tier_warning ?? false}
        apiClient={apiClient}
      />
    );
  } else {
    const s = stubs[view];
    if (s) {
      const activatedAt = activationStatus.activatedAt;
      const cta =
        view === "system" ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <button
              type="button"
              className="tp-btn tp-btn-secondary"
              onClick={() => {
                setDisclosurePromptRequired(false);
                setShowWizard(true);
              }}
            >
              {activationStatus.activated
                ? "Re-run setup wizard"
                : "Run first-run setup wizard"}
            </button>
            {activationStatus.activated && activatedAt && (
              <span
                style={{
                  fontSize: 11,
                  color: "var(--tp-fg-3, #888)",
                }}
              >
                Last activated: {new Date(activatedAt).toLocaleString()}
              </span>
            )}
            {/* Phase 7a D9: operator opt-in after 14-day shadow window */}
            <button
              type="button"
              className="tp-btn tp-btn-secondary"
              onClick={() => setShowNewsActivation(true)}
            >
              Enable news and expanded AI inputs
            </button>
          </div>
        ) : null;
      // Phase 5 a.7: System view gets a dedicated screen with the
      // backup-tests panel + verify button. Other stub views still
      // use StubScreen.
      if (view === "system") {
        screen = <SystemScreen cta={cta} />;
      } else {
        screen = <StubScreen title={s.title} note={s.note} cta={cta} />;
      }
    } else {
      screen = (
        <StubScreen
          title="Unknown"
          note={`No screen registered for ${view}`}
        />
      );
    }
  }

  if (showNewsActivation) {
    return (
      <>
        <SizeGuard />
        <NewsActivationConfirmation
          onDone={() => setShowNewsActivation(false)}
          onCancel={() => setShowNewsActivation(false)}
        />
      </>
    );
  }

  if (showWizard) {
    return (
      <>
        <SizeGuard />
        <FirstRunWizard
          onActivate={() => {
            setShowWizard(false);
            setDisclosurePromptRequired(false);
            setActivationStatus(readLocalActivationStatus());
          }}
          onDefer={() => {
            if (!disclosurePromptRequired) {
              setShowWizard(false);
            }
          }}
          allowDefer={!disclosurePromptRequired}
        />
      </>
    );
  }

  return (
    <>
      <SizeGuard />
      {fixturesActive && (
        <div
          role="alert"
          style={{
            background: "#ffe9a7",
            color: "#553300",
            padding: "0.4rem 1rem",
            fontSize: "0.85rem",
            borderBottom: "1px solid #d8b56b",
          }}
        >
          ⚠ Running with static fixtures — backend NOT connected.
          To run against the real backend, unset
          <code> VITE_USE_FIXTURES</code> and rebuild.
        </div>
      )}
      {/* Stale/degraded data banner (2026-06-10 safety review,
          finding 7): a failed refresh used to set ds.error without
          any visible signal, leaving the operator staring at silently
          stale numbers. */}
      {!fixturesActive &&
        (ds.error !== null || ds.degradedReads.length > 0) && (
          <div
            role="alert"
            style={{
              background: "#ffe9a7",
              color: "#553300",
              padding: "0.4rem 1rem",
              fontSize: "0.85rem",
              borderBottom: "1px solid #d8b56b",
            }}
          >
            {ds.error !== null
              ? `⚠ Data may be stale — last successful refresh ${
                  ds.lastSuccessAt
                    ? ds.lastSuccessAt.toLocaleTimeString("en-US", {
                        hour12: false,
                      })
                    : "never"
                }. (${ds.error})`
              : "⚠ Some panels are degraded."}
            {ds.degradedReads.length > 0 &&
              ` ${ds.degradedReads.length} optional read${
                ds.degradedReads.length === 1 ? "" : "s"
              } failed: ${ds.degradedReads.join(", ")}.`}
          </div>
        )}
      <TopBar
        accounts={accounts}
        status={{ kind: "ok", label: "trading" }}
        ai={3}
        onNav={setView}
        onLogout={DISABLE_AUTH ? undefined : handleLogout}
      />
      {haltBanners.map((b, i) => (
        <HaltBanner key={`${b.variant}-${i}`} banner={b} />
      ))}
      <div className="tp-layout">
        <Sidebar
          active={view}
          onNav={setView}
          pendingCount={pending.length}
          positionsCount={positions.length}
          ptOverdueCount={ptOverdueCount}
          invOverdueCount={invOverdueCount}
        />
        <main className="tp-main">{screen}</main>
      </div>
      {activePending && (
        <ApprovalModal
          pending={activePending}
          isPlacing={placingApprovalId === activePending.approvalId}
          issue={approvalIssue}
          onClose={() => {
            if (placingApprovalId === activePending.approvalId) {
              return;
            }
            approvalReviewRequestRef.current += 1;
            setApprovalIssue(null);
            setApprovalId(null);
            setApprovalOpenStatus("idle");
            setApprovalOpenError(null);
          }}
          onApprove={async (acks) => {
            setApprovalIssue(null);
            try {
              await handleApprovePending(activePending, acks);
            } catch (e) {
              setApprovalIssue(issueFromApprovalError(e));
            }
          }}
          onResize={(riskDollars) =>
            handleResizeApproval(activePending, riskDollars)
          }
          onOpenRouteSettings={() => {
            approvalReviewRequestRef.current += 1;
            setApprovalIssue(null);
            setApprovalId(null);
            setApprovalOpenStatus("idle");
            setApprovalOpenError(null);
            setConfigTab("strategies");
            setView("config");
          }}
        />
      )}
      {approvalId && !activePending && (
        <ApprovalLookupModal
          approvalId={approvalId}
          status={approvalOpenStatus}
          error={approvalOpenError}
          onClose={() => {
            approvalReviewRequestRef.current += 1;
            setApprovalIssue(null);
            setApprovalId(null);
            setApprovalOpenStatus("idle");
            setApprovalOpenError(null);
          }}
        />
      )}
      {loosenLimit && (
        <LoosenConfigModal
          limit={loosenLimit}
          coolingOffDays={coolingOffDays}
          onClose={() => setLoosenKey(null)}
          onSubmit={submitLoosen}
        />
      )}
    </>
  );
};
